import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

function tenantParams(tenantId?: string) {
  return tenantId ? { tenantId } : undefined;
}

export type OrgGuidanceDocJson = {
  type: "doc";
  content: Array<Record<string, unknown>>;
};

export type OrgGuidanceOutlineItem = {
  id: string;
  level: 1 | 2 | 3 | number;
  text: string;
};

export type OrgGuidanceCapabilities = {
  canEdit: boolean;
  canComment: boolean;
  canModerate: boolean;
};

export type OrgGuidanceDocument = {
  id: string;
  tenantId: string;
  title: string;
  bodyJson: OrgGuidanceDocJson;
  bodyText: string;
  outlineJson: OrgGuidanceOutlineItem[];
  contentVersion: number;
  updatedByUserId?: string | null;
  updatedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  capabilities: OrgGuidanceCapabilities;
};

export type OrgGuidanceSideNote = {
  id: string;
  documentId: string;
  authorUserId: string;
  authorName?: string | null;
  body: string;
  markId?: string | null;
  quoteText?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OrgGuidanceCommentReply = {
  id: string;
  commentId: string;
  authorUserId: string;
  authorName?: string | null;
  body: string;
  createdAt: string;
};

export type OrgGuidanceComment = {
  id: string;
  documentId: string;
  authorUserId: string;
  authorName?: string | null;
  kind: "comment" | "edit_request" | string;
  status: "open" | "approved" | "in_progress" | "handled" | "rejected" | string;
  body: string;
  quoteText?: string | null;
  markId?: string | null;
  anchorFrom?: number | null;
  anchorTo?: number | null;
  assigneeUserId?: string | null;
  moderatedByUserId?: string | null;
  moderatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  replies?: OrgGuidanceCommentReply[];
};

export type OrgGuidanceBundle = {
  item: OrgGuidanceDocument;
  sideNotes: OrgGuidanceSideNote[];
  comments: OrgGuidanceComment[];
};

export type OrgGuidanceVersionListItem = {
  id: string;
  documentId: string;
  contentVersion: number;
  title: string;
  note?: string | null;
  createdByUserId?: string | null;
  createdByName?: string | null;
  createdAt: string;
};

export const orgGuidanceApi = {
  get(tenantId?: string): Promise<OrgGuidanceBundle> {
    return wrap(async () => {
      const res = await httpClient.get<OrgGuidanceBundle>("/org-guidance", {
        params: tenantParams(tenantId),
      });
      return res.data;
    });
  },

  update(
    input: {
      contentVersion: number;
      bodyJson: OrgGuidanceDocJson;
      title?: string;
      note?: string | null;
    },
    tenantId?: string,
  ): Promise<OrgGuidanceBundle> {
    return wrap(async () => {
      const res = await httpClient.put<OrgGuidanceBundle>("/org-guidance", input, {
        params: tenantParams(tenantId),
      });
      return res.data;
    });
  },

  listVersions(tenantId?: string): Promise<{ items: OrgGuidanceVersionListItem[] }> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: OrgGuidanceVersionListItem[] }>(
        "/org-guidance/versions",
        { params: tenantParams(tenantId) },
      );
      return res.data;
    });
  },

  restoreVersion(versionId: string, tenantId?: string): Promise<OrgGuidanceBundle> {
    return wrap(async () => {
      const res = await httpClient.post<OrgGuidanceBundle>(
        `/org-guidance/versions/${versionId}/restore`,
        undefined,
        { params: tenantParams(tenantId) },
      );
      return res.data;
    });
  },

  createSideNote(
    input: {
      body: string;
      markId?: string | null;
      quoteText?: string | null;
    },
    tenantId?: string,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: OrgGuidanceSideNote }>(
        "/org-guidance/side-notes",
        input,
        { params: tenantParams(tenantId) },
      );
      return res.data.item;
    });
  },

  deleteSideNote(noteId: string, tenantId?: string) {
    return wrap(async () => {
      await httpClient.delete(`/org-guidance/side-notes/${noteId}`, {
        params: tenantParams(tenantId),
      });
    });
  },

  createComment(
    input: {
      body: string;
      kind?: "comment" | "edit_request";
      quoteText?: string | null;
      markId?: string | null;
    },
    tenantId?: string,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: OrgGuidanceComment }>(
        "/org-guidance/comments",
        input,
        { params: tenantParams(tenantId) },
      );
      return res.data.item;
    });
  },

  reply(commentId: string, body: string, tenantId?: string) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: OrgGuidanceCommentReply }>(
        `/org-guidance/comments/${commentId}/replies`,
        { body },
        { params: tenantParams(tenantId) },
      );
      return res.data.item;
    });
  },

  moderate(
    commentId: string,
    input: {
      status: "open" | "approved" | "in_progress" | "handled" | "rejected";
      replyBody?: string | null;
    },
    tenantId?: string,
  ) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: OrgGuidanceComment }>(
        `/org-guidance/comments/${commentId}`,
        input,
        { params: tenantParams(tenantId) },
      );
      return res.data.item;
    });
  },
};
